
import React, { useState } from 'react';
import { supabase, getErrorMessage } from '../lib/supabase';
import { Mail, Lock, Loader2, ArrowRight, ShieldCheck } from 'lucide-react';

const Auth: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      if (isSignUp) {
        // Fix: Cast supabase.auth to any to bypass Property 'signUp' does not exist error
        const { error } = await (supabase.auth as any).signUp({ email, password });
        if (error) throw error;
        setMessage({ type: 'success', text: 'Verification email sent! Check your inbox.' });
      } else {
        // Fix: Cast supabase.auth to any to bypass Property 'signInWithPassword' does not exist error
        const { error } = await (supabase.auth as any).signInWithPassword({ email, password });
        if (error) throw error;
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: getErrorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8faf7] flex items-center justify-center p-6 relative overflow-hidden">
      {/* Background Subtle Branding Orbs */}
      <div className="absolute top-0 -left-20 w-96 h-96 bg-[#606c38]/5 rounded-full blur-[120px]"></div>
      <div className="absolute bottom-0 -right-20 w-96 h-96 bg-[#ccd5ae]/10 rounded-full blur-[120px]"></div>

      <div className="w-full max-w-md animate-in fade-in zoom-in-95 duration-700">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-black text-[#283618] tracking-tighter mb-2">VIS APP</h1>
          <p className="text-slate-500 font-medium tracking-tight">Professional Registry & Management Suite</p>
        </div>

        <div className="bg-white border border-slate-200 p-10 rounded-[2.5rem] shadow-xl shadow-[#606c38]/5">
          <div className="flex bg-slate-50 p-1 rounded-2xl border border-slate-100 mb-8">
            <button 
              onClick={() => setIsSignUp(false)}
              className={`flex-1 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${!isSignUp ? 'bg-[#606c38] text-white shadow-lg shadow-[#606c38]/20' : 'text-slate-400 hover:text-slate-600'}`}
            >
              Sign In
            </button>
            <button 
              onClick={() => setIsSignUp(true)}
              className={`flex-1 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${isSignUp ? 'bg-[#606c38] text-white shadow-lg shadow-[#606c38]/20' : 'text-slate-400 hover:text-slate-600'}`}
            >
              Register
            </button>
          </div>

          <form onSubmit={handleAuth} className="space-y-6">
            <div>
              <label className="block text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 ml-1">Email Identifier</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
                <input 
                  required
                  type="email"
                  className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-slate-800 outline-none focus:ring-2 focus:ring-[#606c38]/20 focus:bg-white transition-all font-medium text-sm"
                  placeholder="operations@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="block text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 ml-1">Registry Pin</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
                <input 
                  required
                  type="password"
                  className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-slate-800 outline-none focus:ring-2 focus:ring-[#606c38]/20 focus:bg-white transition-all font-medium text-sm"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            {message && (
              <div className={`p-4 rounded-xl text-[10px] font-black uppercase tracking-widest animate-in slide-in-from-top-2 ${message.type === 'success' ? 'bg-[#fefae0] text-[#606c38] border border-[#606c38]/20' : 'bg-red-50 text-red-500 border border-red-100'}`}>
                {message.text}
              </div>
            )}

            <button 
              disabled={loading}
              type="submit"
              className="w-full py-4 bg-[#606c38] hover:bg-[#283618] text-white font-black text-xs uppercase tracking-[0.2em] rounded-2xl shadow-xl shadow-[#606c38]/10 transition-all flex items-center justify-center gap-3 active:scale-[0.98] disabled:opacity-50"
            >
              {loading ? <Loader2 className="animate-spin" size={20} /> : (
                <>
                  {isSignUp ? 'Initialize Profile' : 'Enter Dashboard'}
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          <div className="mt-10 pt-8 border-t border-slate-100">
             <div className="flex items-center gap-4 text-slate-400">
                <div className="p-2 bg-slate-50 rounded-lg text-[#606c38]"><ShieldCheck size={16} /></div>
                <p className="text-[9px] font-bold uppercase tracking-wider leading-relaxed">
                  Secured via industrial-grade encryption. Verified property data protection active.
                </p>
             </div>
          </div>
        </div>

        <p className="mt-8 text-center text-slate-400 text-[10px] font-bold uppercase tracking-widest">
          Vista Inspection Services <span className="text-[#606c38] mx-2">•</span> <span className="text-slate-300">Pro Edition 2.5.0</span>
        </p>
      </div>
    </div>
  );
};

export default Auth;
